import { supabase } from "@/infrastructure/supabase/client";
import type { FeatureRequestAttachmentRow } from "@/infrastructure/supabase/database.types";

const BUCKET = "feature-request-attachments";
const SIGNED_URL_TTL = 60 * 60; // 1 hour

/** Strip path separators / odd chars from a filename for a safe storage key. */
function safeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 120) || "file";
}

export const featureRequestAttachmentsRepository = {
  async listForRequest(requestId: string): Promise<FeatureRequestAttachmentRow[]> {
    const { data, error } = await supabase
      .from("feature_request_attachments")
      .select("*")
      .eq("request_id", requestId)
      .order("created_at", { ascending: true });
    if (error) throw error;
    return (data as FeatureRequestAttachmentRow[] | null) ?? [];
  },

  /** Upload the bytes to Storage, then record the metadata row. */
  async upload(
    requestId: string,
    file: File,
    uploadedBy: string
  ): Promise<FeatureRequestAttachmentRow> {
    const path = `${requestId}/${crypto.randomUUID()}-${safeName(file.name)}`;
    const up = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { contentType: file.type || undefined, upsert: false });
    if (up.error) throw up.error;

    const { data, error } = await supabase
      .from("feature_request_attachments")
      .insert({
        request_id: requestId,
        storage_path: path,
        file_name: file.name,
        content_type: file.type || null,
        size_bytes: file.size,
        uploaded_by: uploadedBy,
      })
      .select("*")
      .single();
    if (error) {
      // Best-effort rollback of the orphaned object if the metadata insert fails.
      await supabase.storage.from(BUCKET).remove([path]);
      throw error;
    }
    return data as FeatureRequestAttachmentRow;
  },

  /** A short-lived signed URL to view/download a private object. */
  async signedUrl(storagePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(storagePath, SIGNED_URL_TTL);
    if (error) throw error;
    return data.signedUrl;
  },

  /**
   * Remove the underlying object first, then the metadata row. Storage-first is
   * deliberate: an orphaned object (bytes with no row to find it) is an
   * unrecoverable leak, whereas a dangling row is recoverable — so if the object
   * removal fails we abort and leave the row for a retry.
   */
  async remove(
    attachment: Pick<FeatureRequestAttachmentRow, "id" | "storage_path">
  ): Promise<void> {
    // storage_path is null for an external (Yango) attachment whose bytes were
    // never fetched into our bucket — there is nothing to remove, just the row.
    if (attachment.storage_path) {
      const rm = await supabase.storage.from(BUCKET).remove([attachment.storage_path]);
      if (rm.error) throw rm.error;
    }
    const { error } = await supabase
      .from("feature_request_attachments")
      .delete()
      .eq("id", attachment.id);
    if (error) throw error;
  },

  /**
   * Best-effort removal of every object for a request — called before the request
   * row is deleted (its FK cascade drops the attachment rows, which would strand
   * the bytes). Errors are swallowed so the request delete still proceeds; a rare
   * orphan (e.g. RLS-denied object owned by another user) is left for a sweep.
   */
  async removeAllForRequest(requestId: string): Promise<void> {
    const { data } = await supabase
      .from("feature_request_attachments")
      .select("storage_path")
      .eq("request_id", requestId);
    const paths = (data as { storage_path: string }[] | null)?.map((a) => a.storage_path) ?? [];
    if (paths.length > 0) await supabase.storage.from(BUCKET).remove(paths);
  },
};
